/**
 * Universal Content Processor for Mathematical and Formatted Text
 * 
 * This utility handles content that comes from JSON databases containing:
 * - LaTeX mathematical expressions ($...$ and   // Handle different LaTeX formats found in JSON:
  // ${...} format - convert to $$...$$ (block math)
  let normalizedText = text.replace(/\$\{([^}]*)\}/g, '$$$1$$');)
 * - Markdown formatting (**bold**, *italic*)
 * - Mixed text with mathematical notation
 * - Numbered and bullet lists
 * - Line breaks and paragraphs
 * 
 * Usage: Can be used throughout the application for any content that needs
 * mathematical rendering - chat messages, dashboard content, documents, etc.
 */

export interface ProcessedContent {
  type: 'text' | 'math-inline' | 'math-block' | 'list-ordered' | 'list-unordered' | 'paragraph' | 'table' | 'latex-env';
  content: string | ProcessedContentBlock[] | TableData | LatexEnvironment;
  key: string;
}

export interface TableData {
  headers: string[];
  rows: string[][];
  alignment?: ('left' | 'center' | 'right')[];
}

export interface LatexEnvironment {
  type: string; // quote, theorem, definition, etc.
  content: ProcessedContentBlock[];
}

export interface ProcessedContentBlock {
  type: 'text' | 'math-inline' | 'math-block';
  content: string;
  key: string;
}

/**
 * Main function to process content with LaTeX and Markdown formatting
 * Handles content exactly as it appears in JSON database format
 */
export const processContent = (rawContent: string): ProcessedContent[] => {
  if (!rawContent || typeof rawContent !== 'string') {
    return [];
  }

  // Normalize line endings and clean up
  const cleanContent = rawContent
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();

  // Split into blocks (paragraphs, lists, etc.)
  const blocks = cleanContent.split('\n\n').filter(block => block.trim());
  
  return blocks.map((block, blockIndex) => {
    const trimmedBlock = block.trim();
    
    // Handle tables (markdown table format with | separators)
    if (trimmedBlock.includes('|') && trimmedBlock.split('\n').length >= 2) {
      const lines = trimmedBlock.split('\n').map(line => line.trim()).filter(line => line);
      
      // Check if this looks like a table (has at least 2 lines with | separators)
      const potentialTableLines = lines.filter(line => line.includes('|') && line.split('|').length >= 3);
      
      if (potentialTableLines.length >= 2) {
        // Parse the table
        const headers: string[] = [];
        const rows: string[][] = [];
        const alignment: ('left' | 'center' | 'right')[] = [];
        
        let headerLine = '';
        let alignmentLine = '';
        let dataStartIndex = 0;
        
        // Find header and alignment lines
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (line.includes('|')) {
            if (!headerLine) {
              headerLine = line;
            } else if (!alignmentLine) {
              // Check if this line looks like a table separator/alignment line
              // Patterns: |---|---|---| or | :--- | :---: | ---: | or |:--|:--:|--:|
              const cleanLine = line.replace(/\s/g, '');
              const isAlignmentLine = /^[|]*[-:]+([|][-:]+)*[|]*$/.test(cleanLine);
              
              if (isAlignmentLine) {
                alignmentLine = line;
                dataStartIndex = i + 1;
                break;
              } else {
                // No alignment line, start data from this line
                dataStartIndex = i;
                break;
              }
            }
          }
        }
        
        // Parse headers
        if (headerLine) {
          const headerCells = headerLine.split('|').map(cell => cell.trim()).filter(cell => cell);
          headers.push(...headerCells);
        }
        
        // Parse alignment
        if (alignmentLine) {
          const alignCells = alignmentLine.split('|').map(cell => cell.trim()).filter(cell => cell);
          alignCells.forEach(cell => {
            if (cell.startsWith(':') && cell.endsWith(':')) {
              alignment.push('center');
            } else if (cell.endsWith(':')) {
              alignment.push('right');
            } else {
              alignment.push('left');
            }
          });
        }
        
        // Parse data rows
        for (let i = dataStartIndex; i < lines.length; i++) {
          const line = lines[i];
          if (line.includes('|')) {
            const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell);
            if (cells.length > 0) {
              rows.push(cells);
            }
          }
        }
        
        if (headers.length > 0 || rows.length > 0) {
          return {
            type: 'table' as const,
            content: {
              headers,
              rows,
              alignment: alignment.length > 0 ? alignment : undefined
            } as TableData,
            key: `block-${blockIndex}`
          };
        }
      }
    }
    
    // Handle numbered lists (1. 2. 3. etc.)
    if (/^\d+\./.test(trimmedBlock)) {
      const listItems = trimmedBlock.split(/\n(?=\d+\.)/);
      const processedItems = listItems.map((item, itemIndex) => {
        const content = item.replace(/^\d+\.\s*/, '');
        return {
          type: 'text' as const,
          content: content,
          key: `${blockIndex}-ol-${itemIndex}`
        };
      });
      
      return {
        type: 'list-ordered' as const,
        content: processedItems,
        key: `block-${blockIndex}`
      };
    }
    
    // Handle LaTeX environments (like \begin{quote}...\end{quote})
    const latexEnvMatch = trimmedBlock.match(/\\begin\{([^}]+)\}([\s\S]*?)\\end\{\1\}/);
    if (latexEnvMatch) {
      const envType = latexEnvMatch[1];
      const envContent = latexEnvMatch[2].trim();
      
      // Process the content inside the environment (may contain math and markdown)
      const processedContent = processInlineContent(envContent);
      
      return {
        type: 'latex-env' as const,
        content: {
          type: envType,
          content: processedContent
        } as LatexEnvironment,
        key: `block-${blockIndex}`
      };
    }
    
    // Handle bullet lists (- item or * item)
    if (/^[-*]\s/.test(trimmedBlock)) {
      const listItems = trimmedBlock.split(/\n(?=[-*]\s)/);
      const processedItems = listItems.map((item, itemIndex) => {
        const content = item.replace(/^[-*]\s*/, '');
        return {
          type: 'text' as const,
          content: content,
          key: `${blockIndex}-ul-${itemIndex}`
        };
      });
      
      return {
        type: 'list-unordered' as const,
        content: processedItems,
        key: `block-${blockIndex}`
      };
    }
    
    // Handle regular paragraphs (may contain multiple lines)
    return {
      type: 'paragraph' as const,
      content: trimmedBlock,
      key: `block-${blockIndex}`
    };
  });
};

/**
 * Process inline content within a text block
 * Handles LaTeX expressions and Markdown formatting
 * Now processes markdown FIRST to preserve italic/bold across LaTeX
 */
export const processInlineContent = (text: string): ProcessedContentBlock[] => {
  if (!text || typeof text !== 'string') {
    return [];
  }

  // Handle different LaTeX formats found in JSON:
  // ${...} format - convert to $...$ (inline math) and unescape backslashes
  let normalizedText = text.replace(/\$\{([^}]*)\}/g, (match, content) => {
    // Unescape double backslashes in the content
    const unescapedContent = content.replace(/\\\\/g, '\\');
    return `$${unescapedContent}$`;
  });
  
  // FIRST: Handle markdown formatting that might span across LaTeX expressions
  // This needs to happen before splitting by LaTeX to preserve italic/bold formatting
  
  // For text that has markdown spanning LaTeX, we need to process it differently
  // Check if we have italic or bold formatting that might span LaTeX
  const hasSpanningMarkdown = /\*[^*]*\$[^$]*\$[^*]*\*/.test(normalizedText) || 
                              /\*\*[^*]*\$[^$]*\$[^*]*\*\*/.test(normalizedText);
  
  if (hasSpanningMarkdown) {
    // Handle markdown first, preserving LaTeX expressions as placeholders
    const latexPlaceholders: string[] = [];
    let placeholderIndex = 0;
    
    // Extract LaTeX expressions and replace with placeholders
    normalizedText = normalizedText.replace(/(\$\$[^$]*\$\$|\$[^$]*\$)/g, (match) => {
      const placeholder = `__LATEX_PLACEHOLDER_${placeholderIndex}__`;
      latexPlaceholders[placeholderIndex] = match;
      placeholderIndex++;
      return placeholder;
    });
    
    // Now process markdown formatting
    normalizedText = processMarkdownText(normalizedText);
    
    // Restore LaTeX expressions
    latexPlaceholders.forEach((latex, index) => {
      normalizedText = normalizedText.replace(`__LATEX_PLACEHOLDER_${index}__`, latex);
    });
    
    // Now split by LaTeX expressions for rendering
    const parts = normalizedText.split(/(\$\$[^$]*\$\$|\$[^$]*\$)/g);
    
    return parts.map((part, index) => {
      // Handle display math ($$...$$)
      if (part.startsWith('$$') && part.endsWith('$$') && part.length > 4) {
        const mathContent = part.slice(2, -2).trim();
        // Unescape double backslashes from JSON format to single backslashes for LaTeX
        const unescapedMathContent = mathContent.replace(/\\\\/g, '\\');
        return {
          type: 'math-block' as const,
          content: unescapedMathContent,
          key: `math-block-${index}`
        };
      }
      
      // Handle inline math ($...$)
      if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
        const mathContent = part.slice(1, -1).trim();
        // Unescape double backslashes from JSON format to single backslashes for LaTeX
        const unescapedMathContent = mathContent.replace(/\\\\/g, '\\');
        return {
          type: 'math-inline' as const,
          content: unescapedMathContent,
          key: `math-inline-${index}`
        };
      }
      
      // Handle regular text (already has markdown processed)
      if (part.trim()) {
        return {
          type: 'text' as const,
          content: part,
          key: `text-${index}`
        };
      }
      
      return {
        type: 'text' as const,
        content: '',
        key: `empty-${index}`
      };
    }).filter(block => block.content.trim() !== '');
  }
  
  // For normal text without spanning markdown, use the original method
  // Split by LaTeX expressions (both inline $...$ and display $$...$$)
  const parts = normalizedText.split(/(\$\$[^$]*\$\$|\$[^$]*\$)/g);
  
  return parts.map((part, index) => {
    // Handle display math ($$...$$)
    if (part.startsWith('$$') && part.endsWith('$$') && part.length > 4) {
      const mathContent = part.slice(2, -2).trim();
      // Unescape double backslashes from JSON format to single backslashes for LaTeX
      const unescapedMathContent = mathContent.replace(/\\\\/g, '\\');
      return {
        type: 'math-block' as const,
        content: unescapedMathContent,
        key: `math-block-${index}`
      };
    }
    
    // Handle inline math ($...$)
    if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
      const mathContent = part.slice(1, -1).trim();
      // Unescape double backslashes from JSON format to single backslashes for LaTeX
      const unescapedMathContent = mathContent.replace(/\\\\/g, '\\');
      return {
        type: 'math-inline' as const,
        content: unescapedMathContent,
        key: `math-inline-${index}`
      };
    }
    
    // Handle regular text (may contain Markdown)
    if (part.trim()) {
      return {
        type: 'text' as const,
        content: part,
        key: `text-${index}`
      };
    }

    return {
      type: 'text' as const,
      content: '',
      key: `empty-${index}`
    };
  }).filter(block => block.content.trim() !== '');
};

/**
 * Process Markdown formatting within text
 * Handles **bold**, *italic*, and other formatting
 */
export const processMarkdownText = (text: string): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  return text
    // Bold text **text** (process first to avoid conflicts)
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic text *text* (improved pattern to handle edge cases)
    .replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>')
    // Handle line breaks within blocks
    .replace(/\n/g, '<br/>')
    // Handle sub-bullets with proper indentation (only at start of line or after line break)
    .replace(/(^|<br\/>)\s{2,}- /g, '$1&nbsp;&nbsp;• ')
    // Handle main bullets (only at start of line or after line break)
    .replace(/(^|<br\/>)- /g, '$1• ')
    // Handle list items with asterisk ONLY when they're clearly list items
    // (at start of line/after break, followed by space and non-asterisk content)
    .replace(/(^|<br\/\>)\*\s+([^*])/g, '$1• $2')
    // Handle indented content (like sub-points)
    .replace(/\s{3,}/g, '&nbsp;&nbsp;&nbsp;')
    // Clean up multiple spaces
    .replace(/&nbsp;{4,}/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
};

/**
 * Validate LaTeX expression
 * Returns true if the expression is likely valid LaTeX
 */
export const validateLatexExpression = (expression: string): boolean => {
  if (!expression || typeof expression !== 'string') {
    return false;
  }

  // Basic validation - check for balanced braces and valid LaTeX patterns
  const braceCount = (expression.match(/\{/g) || []).length - (expression.match(/\}/g) || []).length;
  
  // Should have balanced braces
  if (braceCount !== 0) {
    return false;
  }

  // Check for common LaTeX commands that should be valid
  const commonCommands = [
    '\\sqrt', '\\frac', '\\times', '\\div', '\\sum', '\\int', '\\pi',
    '\\alpha', '\\beta', '\\gamma', '\\theta', '\\phi', '\\leq', '\\geq',
    '\\neq', '\\in', '\\subset', '\\cap', '\\cup', '\\rightarrow', '\\leftarrow'
  ];

  // Allow expressions with common mathematical symbols
  const hasValidContent = /[a-zA-Z0-9+\-=<>\\{}^_\s]/.test(expression);
  
  return hasValidContent;
};

/**
 * Extract all mathematical expressions from content
 * Useful for preprocessing or validation
 */
export const extractMathExpressions = (content: string): { inline: string[], block: string[] } => {
  const inline: string[] = [];
  const block: string[] = [];

  if (!content || typeof content !== 'string') {
    return { inline, block };
  }

  // Find inline math expressions
  const inlineMatches = content.match(/\$([^$]+)\$/g);
  if (inlineMatches) {
    inlineMatches.forEach(match => {
      const expr = match.slice(1, -1);
      if (validateLatexExpression(expr)) {
        inline.push(expr);
      }
    });
  }

  // Find block math expressions
  const blockMatches = content.match(/\$\$([^$]+)\$\$/g);
  if (blockMatches) {
    blockMatches.forEach(match => {
      const expr = match.slice(2, -2);
      if (validateLatexExpression(expr)) {
        block.push(expr);
      }
    });
  }

  // Handle ${...} format
  const altMatches = content.match(/\$\{([^}]+)\}/g);
  if (altMatches) {
    altMatches.forEach(match => {
      const expr = match.slice(2, -1);
      if (validateLatexExpression(expr)) {
        block.push(expr);
      }
    });
  }

  return { inline, block };
};

/**
 * Clean and prepare content for database storage
 * Ensures proper escaping and formatting
 */
export const prepareContentForStorage = (content: string): string => {
  if (!content || typeof content !== 'string') {
    return '';
  }

  return content
    // Ensure proper LaTeX escaping for database storage
    .replace(/\\/g, '\\\\') // Double escape backslashes for JSON storage
    .trim();
};

/**
 * Statistics and analysis for content processing
 */
export const analyzeContent = (content: string) => {
  const mathExpressions = extractMathExpressions(content);
  const wordCount = content.split(/\s+/).filter(word => word.length > 0).length;
  const hasMarkdown = /\*\*.*?\*\*|\*.*?\*/.test(content);
  const hasLists = /^\d+\.|^[-*]\s/m.test(content);
  
  return {
    wordCount,
    mathExpressions: {
      inline: mathExpressions.inline.length,
      block: mathExpressions.block.length,
      total: mathExpressions.inline.length + mathExpressions.block.length
    },
    hasMarkdown,
    hasLists,
    estimatedComplexity: mathExpressions.inline.length + mathExpressions.block.length * 2 + (hasMarkdown ? 1 : 0)
  };
};
